import { ApiError, SessionExpiredError } from "../api/api-error";
/**
 * The D23 failure policy: what a failed send means for the queue.
 *
 * | Failure                                       | Result       |
 * |-----------------------------------------------|--------------|
 * | fetch threw (`TypeError`), timeout/abort      | `retry`      |
 * | HTTP 5xx, 429 (honouring `Retry-After`)       | `retry`      |
 * | HTTP 401, `SessionExpiredError`               | `reauth`     |
 * | any other 4xx                                 | `quarantine` |
 * | anything else                                 | `quarantine` |
 *
 * ── WHY A 401 HERE IS "SIGN IN AGAIN" ──────────────────────────────────────
 * `ApiClient.authorizedFetch` already retries a 401 once with a refresh, so a
 * 401 or a `SessionExpiredError` reaching the outbox means the refresh itself
 * failed: the account's session is gone. The item is not at fault. It stays
 * pending and resends after the next sign-in — never quarantined, never
 * dropped.
 *
 * ── WHY "ANYTHING ELSE" QUARANTINES ────────────────────────────────────────
 * An `ApiError` with a non-error status is `readJson` failing to decode a
 * success (`error.internal`): the server answered in a shape this build does
 * not understand, and retrying forever would hide that. An unknown thrown
 * value is a bug. Quarantine is the visible outcome; retrying silently is not.
 *
 * `TypeError` is the fetch network signature in every engine ("Failed to
 * fetch" in Chromium WebView, "Load failed" in WebKit, "NetworkError …" in
 * Gecko), so every `TypeError` retries.
 */
/** `lastError.messageKey` for a send that got no response. Status is `0`. */
export const NETWORK_ERROR_KEY = "error.network";
/** `lastError.messageKey` for a send the refresh could not authorise. */
export const SESSION_EXPIRED_KEY = "auth.session_expired";
/** `lastError.messageKey` for a thrown value that is not an `ApiError`. */
export const UNKNOWN_ERROR_KEY = "error.internal";
const DELTA_SECONDS = /^\d+$/;
const NETWORK_ERROR_NAMES = new Set([
    "AbortError",
    "NetworkError",
    "TimeoutError",
]);
function isNetworkError(error) {
    if (error instanceof TypeError) {
        return true;
    }
    if (typeof error === "object" && error !== null) {
        const { name } = error;
        return typeof name === "string" && NETWORK_ERROR_NAMES.has(name);
    }
    return false;
}
function retryAfterOf(error) {
    if (typeof error !== "object" || error === null) {
        return;
    }
    const value = error.retryAfterMs;
    return typeof value === "number" && Number.isFinite(value) && value >= 0
        ? value
        : undefined;
}
export function classify(error) {
    if (error instanceof SessionExpiredError) {
        return {
            action: "reauth",
            error: { messageKey: SESSION_EXPIRED_KEY, status: 401 },
        };
    }
    if (error instanceof ApiError) {
        const lastError = {
            messageKey: error.messageKey,
            status: error.status,
        };
        if (error.status === 401) {
            return { action: "reauth", error: lastError };
        }
        if (error.status === 429 || error.status >= 500) {
            const retryAfterMs = retryAfterOf(error);
            return retryAfterMs === undefined
                ? { action: "retry", error: lastError }
                : { action: "retry", error: lastError, retryAfterMs };
        }
        return { action: "quarantine", error: lastError };
    }
    if (isNetworkError(error)) {
        return {
            action: "retry",
            error: { messageKey: NETWORK_ERROR_KEY, status: 0 },
        };
    }
    return {
        action: "quarantine",
        error: { messageKey: UNKNOWN_ERROR_KEY, status: 0 },
    };
}
/**
 * Parse an HTTP `Retry-After` header — delta-seconds or an HTTP date — into
 * milliseconds from `now`. `undefined` for an absent or unreadable header.
 */
export function parseRetryAfter(header, now) {
    if (header === null || header === undefined) {
        return;
    }
    const trimmed = header.trim();
    if (DELTA_SECONDS.test(trimmed)) {
        return Number(trimmed) * 1000;
    }
    const at = Date.parse(trimmed);
    if (Number.isNaN(at)) {
        return;
    }
    return Math.max(0, at - now.getTime());
}
//# sourceMappingURL=classify.js.map